import React, { useState } from 'react';
import {
  useListTestCategoriesQuery,
  useCreateTestCategoryMutation,
  useUpdateTestCategoryMutation,
  useDeleteTestCategoryMutation,
  useListDrugComponentsQuery,
  useCreateDrugComponentMutation,
  useUpdateDrugComponentMutation,
  useDeleteDrugComponentMutation,
  type TestCategory,
  type DrugComponent,
} from '@/api/masterDataApi';
import { Plus, Edit2, Trash2, Settings2, TestTube2, Layers, Search } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

const MasterDataManagement: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'categories' | 'components'>('categories');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<TestCategory | DrugComponent | null>(null);
  
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'ALL' | 'ACTIVE' | 'INACTIVE'>('ALL');
  
  // Queries
  const { data: categories, isLoading: catLoading, error: catError } = useListTestCategoriesQuery();
  const { data: components, isLoading: compLoading, error: compError } = useListDrugComponentsQuery();

  // Mutations
  const [createCategory, { isLoading: isCreatingCategory }] = useCreateTestCategoryMutation();
  const [updateCategory, { isLoading: isUpdatingCategory }] = useUpdateTestCategoryMutation();
  const [deleteCategory, { isLoading: isDeletingCategory }] = useDeleteTestCategoryMutation();
  
  const [createComponent, { isLoading: isCreatingComponent }] = useCreateDrugComponentMutation();
  const [updateComponent, { isLoading: isUpdatingComponent }] = useUpdateDrugComponentMutation();
  const [deleteComponent, { isLoading: isDeletingComponent }] = useDeleteDrugComponentMutation();

  const filteredCategories = React.useMemo(() => {
    if (!categories) return [];
    return categories.filter(c => {
      const matchesSearch = c.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
                            (c.description || '').toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStatus = statusFilter === 'ALL' || c.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [categories, searchQuery, statusFilter]);

  const filteredComponents = React.useMemo(() => {
    if (!components) return [];
    return components.filter(c => {
      const matchesSearch = c.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
                            c.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
                            (c.description || '').toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStatus = statusFilter === 'ALL' || c.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [components, searchQuery, statusFilter]);

  const isLoading = isCreatingCategory || isUpdatingCategory || isCreatingComponent || isUpdatingComponent;

  const handleSave = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const data = Object.fromEntries(formData.entries());

    try {
      if (activeTab === 'categories') {
        if (editingItem) {
          await updateCategory({ id: editingItem.id, data }).unwrap();
          toast.success('Test category updated successfully');
        } else {
          await createCategory(data).unwrap();
          toast.success('Test category created successfully');
        }
      } else {
        if (editingItem) {
          await updateComponent({ id: editingItem.id, data }).unwrap();
          toast.success('Drug component updated successfully');
        } else {
          await createComponent(data).unwrap();
          toast.success('Drug component created successfully');
        }
      }
      setIsModalOpen(false);
      setEditingItem(null);
    } catch (error: any) {
      console.error('Failed to save:', error);
      const errorMessage = error?.data?.message || error?.message || 'Failed to save. Please try again.';
      toast.error(errorMessage);
    }
  };

  const handleDelete = async (id: number, name: string) => {
    if (!confirm(`Are you sure you want to delete "${name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      if (activeTab === 'categories') {
        await deleteCategory(id).unwrap();
        toast.success('Test category deleted successfully');
      } else {
        await deleteComponent(id).unwrap();
        toast.success('Drug component deleted successfully');
      }
    } catch (error: any) {
      console.error('Failed to delete:', error);
      const errorMessage = error?.data?.message || error?.message || 'Failed to delete. Please try again.';
      toast.error(errorMessage);
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto min-h-screen bg-slate-50/30 dark:bg-transparent">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-4xl font-black text-slate-900 dark:text-slate-100 flex items-center gap-3 tracking-tight">
            <div className="p-2 bg-blue-600 rounded-2xl shadow-lg ring-4 ring-blue-50">
              <Settings2 className="w-8 h-8 text-white" />
            </div>
            Master Data
          </h1>
          <p className="text-slate-500 mt-2 font-medium">Configure global test categories and drug components.</p>
        </div>
        <Button
          onClick={() => {
            setEditingItem(null);
            setIsModalOpen(true);
          }}
          className="bg-blue-600 hover:bg-blue-700 text-white shadow-xl shadow-blue-200 dark:shadow-none h-12 px-6 rounded-xl hover:scale-105 transition-all"
        >
          <Plus className="w-5 h-5 mr-2" />
          Add {activeTab === 'categories' ? 'Category' : 'Component'}
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={(val) => setActiveTab(val as any)} className="space-y-6">
        <TabsList className="bg-slate-100 dark:bg-slate-800 p-1 rounded-xl h-12 w-full max-w-md shadow-inner border border-slate-200 dark:border-slate-700">
          <TabsTrigger value="categories" className="rounded-lg data-[state=active]:bg-white dark:data-[state=active]:bg-slate-900 data-[state=active]:shadow-sm data-[state=active]:text-blue-600 flex items-center gap-2 h-10 w-full font-bold">
            <Layers className="w-4 h-4" />
            Test Categories
          </TabsTrigger>
          <TabsTrigger value="components" className="rounded-lg data-[state=active]:bg-white dark:data-[state=active]:bg-slate-900 data-[state=active]:shadow-sm data-[state=active]:text-blue-600 flex items-center gap-2 h-10 w-full font-bold">
            <TestTube2 className="w-4 h-4" />
            Drug Components
          </TabsTrigger>
        </TabsList>

        <div className="flex flex-col sm:flex-row gap-4 mb-2">
          <div className="relative flex-1 group">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 group-focus-within:text-blue-500 transition-colors" />
            <Input
              placeholder={activeTab === 'categories' ? "Search categories..." : "Search components..."}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-11 h-12 bg-white dark:bg-slate-900 border-slate-200 rounded-xl shadow-sm focus-visible:ring-blue-500"
            />
          </div>
          <div className="sm:w-56">
            <Select value={statusFilter} onValueChange={(val) => setStatusFilter(val as any)}>
              <SelectTrigger className="h-12 border-slate-200 rounded-xl bg-white dark:bg-slate-900 shadow-sm focus:ring-blue-500">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent className="rounded-xl border-slate-200 shadow-2xl">
                <SelectItem value="ALL">All Status</SelectItem>
                <SelectItem value="ACTIVE">Active Only</SelectItem>
                <SelectItem value="INACTIVE">Inactive Only</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        
        <div className="flex items-center gap-2 text-sm text-slate-500 font-medium px-1 mb-2">
          <Badge variant="outline" className="bg-slate-50 text-slate-600 border-slate-200 px-2 py-0.5">
            {activeTab === 'categories' ? filteredCategories.length : filteredComponents.length} {activeTab === 'categories' ? 'categories' : 'components'}
          </Badge>
          {activeTab === 'components' && components && (
            <span className="opacity-70">/ Total components: {components.length}</span>
          )}
        </div>

        <Card className="border-slate-200 dark:border-slate-800 shadow-xl shadow-slate-200/50 dark:shadow-none bg-white/50 dark:bg-slate-900/50 backdrop-blur-sm rounded-2xl overflow-hidden border-2">
          <CardContent className="p-0">
            <Table>
              <TableHeader className="bg-slate-50/80 dark:bg-slate-800/80 backdrop-blur-md">
                <TableRow className="border-slate-200 hover:bg-transparent">
                  <TableHead className="px-6 h-14 font-black text-slate-900 dark:text-slate-100 uppercase tracking-wider text-[11px]">
                    {activeTab === 'categories' ? 'Category Name' : 'Component'}
                  </TableHead>
                  <TableHead className="px-6 h-14 font-black text-slate-900 dark:text-slate-100 uppercase tracking-wider text-[11px]">Description</TableHead>
                  <TableHead className="px-6 h-14 font-black text-slate-900 dark:text-slate-100 uppercase tracking-wider text-[11px]">Status</TableHead>
                  <TableHead className="px-6 h-14 font-black text-slate-900 dark:text-slate-100 uppercase tracking-wider text-[11px] text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {activeTab === 'categories' ? (
                  catLoading ? (
                    <TableRow><TableCell colSpan={4} className="h-40 text-center text-slate-400 font-medium italic">Loading categories...</TableCell></TableRow>
                  ) : catError ? (
                    <TableRow><TableCell colSpan={4} className="h-40 text-center text-rose-500 font-bold">Error loading categories. Please refresh.</TableCell></TableRow>
                  ) : filteredCategories?.length === 0 ? (
                    <TableRow><TableCell colSpan={4} className="h-40 text-center text-slate-400 font-medium">No categories found matching criteria.</TableCell></TableRow>
                  ) : (
                    filteredCategories?.map((cat) => (
                      <TableRow key={cat.id} className="border-slate-100 dark:border-slate-800 hover:bg-slate-50/50 dark:hover:bg-slate-800/30 transition-colors group">
                        <TableCell className="px-6 py-5 font-bold text-slate-900 dark:text-slate-100">{cat.name}</TableCell>
                        <TableCell className="px-6 py-5 text-slate-600 dark:text-slate-400 max-w-xs truncate">{cat.description || '-'}</TableCell>
                        <TableCell className="px-6 py-5 text-slate-600">
                          <Badge 
                            variant={cat.status === 'ACTIVE' ? 'default' : 'secondary'}
                            className={`${cat.status === 'ACTIVE' ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100' : 'bg-slate-100 text-slate-600 hover:bg-slate-100'} font-bold border-0`}
                          >
                            {cat.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="px-6 py-5 text-right">
                          <div className="flex justify-end gap-1 opacity-60 group-hover:opacity-100 transition-opacity">
                            <Button 
                              variant="ghost" 
                              size="icon"
                              onClick={() => { setEditingItem(cat); setIsModalOpen(true); }} 
                              className="h-9 w-9 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30"
                              disabled={isDeletingCategory}
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon"
                              onClick={() => handleDelete(cat.id, cat.name)} 
                              className="h-9 w-9 text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/30"
                              disabled={isDeletingCategory}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )
                ) : (
                  compLoading ? (
                    <TableRow><TableCell colSpan={4} className="h-40 text-center text-slate-400 italic">Loading components...</TableCell></TableRow>
                  ) : compError ? (
                    <TableRow><TableCell colSpan={4} className="h-40 text-center text-rose-500 font-bold">Error loading components.</TableCell></TableRow>
                  ) : filteredComponents?.length === 0 ? (
                    <TableRow><TableCell colSpan={4} className="h-40 text-center text-slate-400 font-medium">No components found.</TableCell></TableRow>
                  ) : (
                    filteredComponents?.map((comp) => (
                      <TableRow key={comp.id} className="border-slate-100 dark:border-slate-800 hover:bg-slate-50/50 dark:hover:bg-slate-800/30 transition-colors group">
                        <TableCell className="px-6 py-5">
                          <div className="font-bold text-slate-900 dark:text-slate-100">{comp.name}</div>
                          <div className="text-[10px] font-black text-slate-400 tracking-widest mt-0.5">{comp.code}</div>
                        </TableCell>
                        <TableCell className="px-6 py-5 text-slate-600 dark:text-slate-400 max-w-xs truncate">{comp.description || '-'}</TableCell>
                        <TableCell className="px-6 py-5">
                          <Badge 
                            variant={comp.status === 'ACTIVE' ? 'default' : 'secondary'}
                            className={`${comp.status === 'ACTIVE' ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100' : 'bg-slate-100 text-slate-600 hover:bg-slate-100'} font-bold border-0`}
                          >
                            {comp.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="px-6 py-5 text-right">
                           <div className="flex justify-end gap-1 opacity-60 group-hover:opacity-100 transition-opacity">
                            <Button 
                              variant="ghost" 
                              size="icon"
                              onClick={() => { setEditingItem(comp); setIsModalOpen(true); }} 
                              className="h-9 w-9 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30"
                              disabled={isDeletingComponent}
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon"
                              onClick={() => handleDelete(comp.id, comp.name)} 
                              className="h-9 w-9 text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/30"
                              disabled={isDeletingComponent}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </Tabs>

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="sm:max-w-md rounded-2xl border-2">
          <DialogHeader>
            <DialogTitle className="text-xl font-black tracking-tight">
              {editingItem ? 'Edit' : 'Add'} {activeTab === 'categories' ? 'Category' : 'Component'}
            </DialogTitle>
            <DialogDescription className="text-slate-500 font-medium">
              Enter the details for {activeTab === 'categories' ? 'test category' : 'drug component'} below.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-5 pt-4">
            {activeTab === 'components' && (
              <div className="space-y-1.5">
                <label className="text-sm font-bold text-slate-700 dark:text-slate-300 ml-1">Code (e.g. THC)</label>
                <Input
                  name="code"
                  defaultValue={(editingItem as DrugComponent)?.code}
                  required
                  placeholder="Enter component code"
                  className="rounded-xl bg-slate-50 focus-visible:ring-blue-500"
                />
              </div>
            )}
            <div className="space-y-1.5">
              <label className="text-sm font-bold text-slate-700 dark:text-slate-300 ml-1">Name</label>
              <Input
                name="name"
                defaultValue={editingItem?.name}
                required
                placeholder="Enter display name"
                className="rounded-xl bg-slate-50 focus-visible:ring-blue-500"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-bold text-slate-700 dark:text-slate-300 ml-1">Description</label>
              <textarea
                name="description"
                defaultValue={editingItem?.description}
                rows={3}
                className="w-full h-24 px-4 py-3 bg-slate-50 border-2 border-slate-100 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all resize-none text-sm"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-bold text-slate-700 dark:text-slate-300 ml-1">Status</label>
              <Select name="status" defaultValue={editingItem?.status || 'ACTIVE'}>
                <SelectTrigger className="rounded-xl bg-slate-50">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent className="rounded-xl">
                  <SelectItem value="ACTIVE">ACTIVE</SelectItem>
                  <SelectItem value="INACTIVE">INACTIVE</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <DialogFooter className="gap-3 sm:gap-0 pt-6">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsModalOpen(false)}
                className="rounded-xl px-6 font-bold"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl px-8 font-black shadow-lg shadow-blue-100 dark:shadow-none transition-all active:scale-95"
                disabled={isLoading}
              >
                {isLoading ? 'Saving...' : 'Save Changes'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MasterDataManagement;
